import { EventEmitter } from "@angular/core";
import { Item } from "@pnp/sp/items";
import { ErrorService } from "src/app/services/error.service";
import { EventBrokerService } from "src/app/services/event-broker.service";
import { getValue, iterableMap } from "src/app/services/utils";

export const trueFunction = () => true;

export type FilterMode = 'FilterBy' | 'SeeAll' | 'MultipleProperties';


export interface PropertyFilterComponent {
  property: FilterProperty;
  filterChanged: EventEmitter<void>;
  filterMode:  'FilterBy' | 'SeeAll' | 'MultipleProperties';
  filterService: ListFilterService;
  load(): void;
}

export interface FilterPropertyChanged {
  sender: any;
  property: FilterProperty;
}

export interface FilterProperty {
  Path?: string;
  DisplayName?: string;
  Type?: 'string' | 'Date' | 'number' | 'People';
  Description?: string;
  Title?: string;
  IsVisibleOnMultiplePropertyFilter?: boolean;
  DataBag?: any;
  IsFiltered?: boolean;
}


export interface Option {
  DisplayName: string;
  Value: string | Date | number;
  IsChecked: boolean;
  Id: string;
}


export interface StringDataBag {
  IsMeSelected?: boolean;
  MeValue?: string;
  SelectedOptions?: Option[];
  SelectedOptionsHistory?: Option[];
}

export enum DateFilterMode {
  Slider,
  Range,
  SpecificDates
}

export interface DateDataBag {
  Mode?: DateFilterMode;
  From?: Date;
  To?: Date;
  SelectedOptions?: Option[];
  SelectedOptionsHistory?: Option[];
}

export class ListFilterService {
  #properties: FilterProperty[];
  get properties(): FilterProperty[] {
    return this.#properties
  }

  #items: any[];

  get items(): any[] {
    return this.#items;
  }

  constructor(properties: FilterProperty[], items: any[], private eventBroker: EventBrokerService, private errorService:ErrorService) {
  try{
    this.#properties = properties as FilterProperty[];
    this.#properties.forEach(x => {
      x.Title = x.Title || "Filter by";
      ListFilterService.ensureDataBag(x);
    });
    this.#items = items;
  }
  catch(error) {
    this.errorService.show('Failed to get filter data', error);
  }
  }

  static ensureDataBag(property: FilterProperty): any {
    try{
    switch (property.Type) {
      case 'Date':
        property.DataBag = property.DataBag || {
          Mode: DateFilterMode.Slider,
          SelectedOptions: [],
          SelectedOptionsHistory: []
        } as DateDataBag;
        break;
      case 'string':
      case 'People':
      case 'number':
        property.DataBag = property.DataBag || {
          SelectedOptions: [],
          SelectedOptionsHistory: [],
          IsMeSelected: false
        } as StringDataBag;
        break;
      default:
        property.DataBag = property.DataBag || {};
    }
    return property.DataBag;
  }
  catch(error) {
    console.log('Failed to get filter data', error);
  }
  }

  getFilterFunction(exceptProperty?: FilterProperty): (item: any) => boolean {
   
    if (!this.#properties || this.#properties.length === 0) return trueFunction;
    const filterFunctions = [] as ((item: any) => boolean)[];
    for (const property of this.#properties) {
      if (!exceptProperty || property !== exceptProperty ) {
        const filterFunction = this.getPropertyFilterFunction(property);
        if (filterFunction !== trueFunction) filterFunctions.push(filterFunction);
      }
    }
    if (filterFunctions.length === 0) return trueFunction;
    return (item) => {
      for (const f of filterFunctions) {
        if (!f(item)) return false;
      }
      return true;
    }
 
  }

  getPropertyFilterFunction(property: FilterProperty): (item: any) => boolean {
   
    switch (property.Type) {
      case "Date": return this.getDateFilterFunction(property);
      case "People":return this.getPeopleFilterFunction(property);
      case "number":
      case "string": return this.getStringOrNumberFilterFunction(property);
    }
 
  }

  private getStringOrNumberFilterFunction(property: FilterProperty): (item: any) => boolean {
    const dataBag = property.DataBag as StringDataBag;
    const selectedOptions = dataBag && dataBag.SelectedOptions;
    const path = property.Path.split('.');
    if (!dataBag.IsMeSelected && (!selectedOptions || selectedOptions.length === 0)) return trueFunction;
    const selectedOptionValues = selectedOptions
      && new Set<string | number>(iterableMap(selectedOptions, op => op.Value as (string | number)))
      || new Set<string | number>();
    if (dataBag.IsMeSelected) selectedOptionValues.add(dataBag.MeValue?.toLowerCase());
    return (item: any) => selectedOptionValues.has(getValue(item, path))
  
  }
  private getPeopleFilterFunction(property: FilterProperty): (item: any) => boolean {
    
    const dataBag = property.DataBag as StringDataBag;
    const selectedOptions = dataBag && dataBag.SelectedOptions;
    const path = property.Path.split('.');
    if (!dataBag.IsMeSelected && (!selectedOptions || selectedOptions.length === 0)) return trueFunction;
    const selectedOptionValues = selectedOptions
      && new Set<string | number>(iterableMap(selectedOptions, op => op.Value as (string | number)))
      || new Set<string | number>();
    if (dataBag.IsMeSelected) selectedOptionValues.add(dataBag.MeValue?.toLowerCase());
   return (item: any) => selectedOptionValues.has(getValue(item, path))
  
  }
  private getDateFilterFunction(property: FilterProperty): (item: any) => boolean {
   
    const dataBag = property.DataBag as DateDataBag;
    const path = property.Path.split('.');
    switch (dataBag.Mode) {
      case DateFilterMode.Range:
      case DateFilterMode.Slider:
        if (!dataBag.From && !dataBag.To) return trueFunction;
        return (item: any) => {
          const propertyValue = new Date(getValue(item, path));
          if (dataBag.From && (!propertyValue || propertyValue < dataBag.From)) return false;
          if (dataBag.To && (propertyValue >= dataBag.To)) return false;
          return true;
        };
      case DateFilterMode.SpecificDates:
        if (!dataBag.SelectedOptions || dataBag.SelectedOptions.length == 0) return trueFunction;
        return (item: any) => {
          const propertyValue = new Date(getValue(item, path));
          return dataBag.SelectedOptions.findIndex(
            op => op.Value === null && !propertyValue ||
              op.Value && propertyValue && (new Date(op.Value)).getFullYear() === propertyValue.getFullYear()
              && (new Date(op.Value)).getMonth() === propertyValue.getMonth()
              && (new Date(op.Value)).getDate() === propertyValue.getDate()
              ) >= 0;
        };
    }
 
  }

  getFiltedItems(exceptProperty?: FilterProperty, items?: any[]) {
    const filterFunction = this.getFilterFunction(exceptProperty);
    items = items || this.#items;
    return items.filter(filterFunction);
  }

  static clear(properties: FilterProperty[]) {
    try{
    properties.forEach(x => {
      x.Description = undefined;
      x.Title = 'Filter by';
      x.IsFiltered = false;
      this.ensureDataBag(x);
      switch (x.Type) {
        case 'Date': {
            const bag = x.DataBag as DateDataBag;
            bag.Mode = DateFilterMode.Slider;
            bag.SelectedOptions = [];
            bag.SelectedOptionsHistory = [];
            bag.From = undefined;
            bag.To = undefined;
          }
          break;
        case 'string':
        case 'People': {
            const bag = x.DataBag as StringDataBag;
            bag.SelectedOptions = [];
            bag.SelectedOptionsHistory = [];
            bag.IsMeSelected = false;
          }
          break;
        default:
          x.DataBag = {};
      }
    });
  }
  catch(error) {
    console.log('Failed to get filter data', error);
  }
  }

  clear() {
    ListFilterService.clear(this.#properties);
    this.eventBroker.publish('FilterCleared', this);
  }

  private static removeOption(property: FilterProperty, option: Option): void {
   try{
    const bag = property.DataBag as StringDataBag;
    const selectedIndex = bag.SelectedOptions.findIndex(x => x.Value === option.Value);
    if (selectedIndex < 0) return;
    bag.SelectedOptions.splice(selectedIndex, 1);
    if (bag.SelectedOptions.length === 0) property.IsFiltered = false;
    bag.SelectedOptionsHistory.push(option);
  }
  catch(error) {
    console.log('Failed to get filter data', error);
  }
  }

  
  /** Add the option to SelectedOption if it is not already there and removes it from history */
  private static addOption(property: FilterProperty, option: Option): void {
   try{
    const bag = property.DataBag as StringDataBag;
    const selectedIndex = bag.SelectedOptions.findIndex(x => x.Value === option.Value);
    if (selectedIndex >= 0) return;
    bag.SelectedOptions.push(option);
    property.IsFiltered = true;
    const historyIndex = bag.SelectedOptionsHistory.findIndex(x => x.Value === option.Value);
    if (historyIndex >= 0) bag.SelectedOptionsHistory.splice(historyIndex, 1);
  }
  catch(error) {
    console.log('Failed to get filter data', error);
  }
  }

  static updateOption(property: FilterProperty, option: Option): void {
    try{
    if (option.IsChecked) {
      ListFilterService.addOption(property, option);
    }
    else {
      ListFilterService.removeOption(property, option);
    }
  }
  catch(error) {
    console.log('Failed to get filter data', error);
  }
  }

  static isFiltered(properties: FilterProperty[]) : boolean {
    return properties.some(p => p.IsFiltered);
  }

  get isFiltered(): boolean {
    return ListFilterService.isFiltered(this.#properties);
  }
}
